import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

type Patch = Record<string, string | number | boolean | null | undefined>;

/**
 * Read/write URL query parameters without clobbering unrelated ones. Table view state (search,
 * filters, sort, page) lives here so any view of a list can be shared as a link.
 */
export function useQueryState(): [URLSearchParams, (patch: Patch, opts?: { replace?: boolean }) => void] {
  const [sp, setSp] = useSearchParams();
  const set = useCallback(
    (patch: Patch, opts: { replace?: boolean } = { replace: true }) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === undefined || v === '' || v === false) next.delete(k);
            else next.set(k, v === true ? '1' : String(v));
          }
          return next;
        },
        { replace: opts.replace ?? true },
      );
    },
    [setSp],
  );
  return [sp, set];
}

/** Namespaced key helper so two tables on one page do not collide (`inv.q`, `inv.sort`). */
export function useKeyed(prefix?: string): (name: string) => string {
  return useMemo(() => (name: string) => (prefix ? `${prefix}.${name}` : name), [prefix]);
}

/** A copy of `prev` with one query parameter set, or removed when `value` is null. */
export function withParam(prev: URLSearchParams, key: string, value: string | null): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (value === null) next.delete(key);
  else next.set(key, value);
  return next;
}
